import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { dateTimeToMonDDYYYYWithTime, convertTo12HourFormat } from '../utils/dateUtils';
import AssignmentViewer from '../components/AssignmentViewer';
import ProjectComments from '../components/ProjectComments';
import { getValidEnrollment } from '../utils/enrollmentUtils';

const TABS = [
  'Description',
  'Assignments',
  'Procedures',
  'Objectives',
  'Standards',
  'Skills',
  'Concentrations',
  'Related Events',
  'Other Resources'
];

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ProjectsDirectory = ({ userData }) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [concentrationFilter, setConcentrationFilter] = useState('');
  const [concentrations, setConcentrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [concentrationInitialized, setConcentrationInitialized] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailProject, setDetailProject] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('Assignments');
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const [taxonomy, setTaxonomy] = useState({ objectives: [], standards: [], skills: [] });
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [taxonomyItems, setTaxonomyItems] = useState([]);
  const isStudentRole = isStudent(userData);
  const [gradeMode, setGradeMode] = useState(false);
  const [assignmentSubmissionEnrollment, setAssignmentSubmissionEnrollment] = useState(null);
  const [showFlagModal, setShowFlagModal] = useState(false);

  // Check URL params on mount or change
  useEffect(() => {
    const command = query.get('command');
    const project_code = query.get('project_code');
    const assignmentSubmission = query.get('assignmentSubmission');
    const projectSubmission = query.get('projectSubmission');
    if ((command === 'displayProject' || command === 'grade' || command === 'gradeButRestricted' || command === 'preview' || command === 'evaluate') && (project_code || (command === 'grade' && assignmentSubmission) || (command === 'gradeButRestricted' && assignmentSubmission) || (command === 'evaluate' && projectSubmission))) {
      setShowDetail(true);
      setActiveTab('Assignments');
      if (command === 'grade' && assignmentSubmission) {
        fetchProjectDetailByAssignmentSubmission(assignmentSubmission);
      } else if (command === 'gradeButRestricted' && assignmentSubmission) {
        fetchProjectDetailByAssignmentSubmission(assignmentSubmission);
      } else if (command === 'evaluate' && projectSubmission) {
        fetchProjectDetailByProjectSubmission(projectSubmission);
      } else {
        fetchProjectDetail(project_code);
      }
      setGradeMode(command === 'grade' || command === 'evaluate');
    } else {
      setShowDetail(false);
      setDetailProject(null);
      setTaxonomy({ objectives: [], standards: [], skills: [] });
      setRelatedEvents([]);
      setGradeMode(false);
    }
    // eslint-disable-next-line
  }, [location.search]);

  // Fetch concentrations and taxonomy items on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Fetch concentrations
        const concRes = await axios.get('/api/concentrations', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setConcentrations(concRes.data.concentrations || []);
        
        // Fetch taxonomy items
        const taxRes = await axios.get('/api/taxonomy/all', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const taxonomyItemsData = taxRes.data.items || [];
        setTaxonomyItems(taxonomyItemsData);
        console.log('ProjectsDirectory - Loaded taxonomy items:', {
          count: taxonomyItemsData.length,
          sample: taxonomyItemsData.slice(0, 3)
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        setConcentrations([]);
        setTaxonomyItems([]);
      }
    };
    fetchData();
  }, []);

  // Set default concentration filter for students
  useEffect(() => {
    if (isStudentRole && userData && userData.accesscards) {
      const validEnrollment = getValidEnrollment(userData);
      if (validEnrollment && validEnrollment.concentration) {
        setConcentrationFilter(validEnrollment.concentration);
      }
    }
    setConcentrationInitialized(true);
  }, [isStudentRole, userData]);

  // Helper function to get concentration name from id
  const getConcentrationName = (id) => {
    if (!id) return '';
    const found = concentrations.find(c => c.id === id);
    return found ? found.title : id;
  };

  // Helper function to merge taxonomy data from both old and new sources
  const mergeTaxonomyData = (project, taxonomyItems) => {
    const merged = { objectives: [], standards: [], skills: [] };
    const seenIds = new Set();

    // Helper function to add item if not already seen
    const addIfNotSeen = (item, type) => {
      if (item && item.id && !seenIds.has(item.id)) {
        seenIds.add(item.id);
        merged[type].push(item);
      }
    };

    // Process new taxonomy field (JSONB)
    if (project.taxonomy && Array.isArray(project.taxonomy)) {
      project.taxonomy.forEach(taxItem => {
        if (taxItem.taxonomy_id) {
          const fullTaxonomyItem = taxonomyItems.find(item => item.id === taxItem.taxonomy_id);
          if (fullTaxonomyItem) {
            const typeKey = fullTaxonomyItem.type.toLowerCase() + 's';
            if (merged[typeKey]) {
              addIfNotSeen(fullTaxonomyItem, typeKey);
            }
          }
        }
      });
    }

    // Process old separate fields (backward compatibility)
    if (project.objectives && Array.isArray(project.objectives)) {
      project.objectives.forEach(objId => {
        const fullTaxonomyItem = taxonomyItems.find(item => item.id === objId);
        if (fullTaxonomyItem) {
          addIfNotSeen(fullTaxonomyItem, 'objectives');
        }
      });
    }

    if (project.standards && Array.isArray(project.standards)) {
      project.standards.forEach(stdId => {
        const fullTaxonomyItem = taxonomyItems.find(item => item.id === stdId);
        if (fullTaxonomyItem) {
          addIfNotSeen(fullTaxonomyItem, 'standards');
        }
      });
    }

    if (project.skills && Array.isArray(project.skills)) {
      project.skills.forEach(skillId => {
        const fullTaxonomyItem = taxonomyItems.find(item => item.id === skillId);
        if (fullTaxonomyItem) {
          addIfNotSeen(fullTaxonomyItem, 'skills');
        }
      });
    }

    return merged;
  };

  // Get student's default concentration
  const getStudentDefaultConcentration = () => {
    if (!isStudentRole || !userData || !userData.accesscards) return null;
    const validEnrollment = getValidEnrollment(userData);
    return validEnrollment?.concentration || null;
  };

  // Helper function to format created date
  const formatCreatedDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to check if user is admin
  const isAdmin = (userData) => {
    if (!userData || !userData.accesscards || !userData.default_accesscard_id) return false;
    const card = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
    return card && card.role && card.role.includes('Admin');
  };

  // Helper function to check if user can see flags (admin or project creator)
  const canSeeFlags = (userData, project) => {
    if (!userData || !project) return false;
    
    // Admin can always see flags
    if (isAdmin(userData)) return true;
    
    // Non-student project creators can see flags
    if (!isStudentRole && project.createdby === userData.account_id) return true;
    
    return false;
  };

  // Helper function to get flag button type and relevant role
  const getFlagButtonInfo = (userData, project) => {
    if (isAdmin(userData)) {
      return { buttonText: '🚩 Flag Project', relevantRole: 'Admin', defaultType: 'Flag' };
    } else if (!isStudentRole && project.createdby === userData.account_id) {
      return { buttonText: '🚩 Flags', relevantRole: 'Instructor', defaultType: 'Flag-General' };
    }
    return null;
  };

  // Fetch projects for list
  useEffect(() => {
    if (showDetail || !concentrationInitialized) return;
    
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const url = concentrationFilter 
          ? `/api/projects?concentration=${concentrationFilter}`
          : '/api/projects';
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Filter out projects with preventsubmissions set to true
        const filteredProjects = (res.data.projects || []).filter(project => !project.preventsubmissions);
        setProjects(filteredProjects);
        setFilteredProjects(filteredProjects);
      } catch (err) {
        setProjects([]);
        setFilteredProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [showDetail, concentrationFilter, concentrationInitialized]);

  // Search filter
  useEffect(() => {
    if (!search) {
      setFilteredProjects(projects);
    } else {
      const s = search.toLowerCase();
      setFilteredProjects(projects.filter(p =>
        (p.title && p.title.toLowerCase().includes(s)) ||
        (p.code && p.code.toLowerCase().includes(s))
      ));
    }
  }, [search, projects]);

  async function fetchProjectDetail(code) {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/projects/display/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailProject(res.data.project || null);
      setTaxonomy(res.data.taxonomy || { objectives: [], standards: [], skills: [] });
      setRelatedEvents(res.data.relatedEvents || []);
    } catch (err) {
      setDetailProject(null);
      setTaxonomy({ objectives: [], standards: [], skills: [] });
      setRelatedEvents([]);
    } finally {
      setDetailLoading(false);
    }
  }

  // Fetch project detail by assignmentSubmission id (for grade mode)
  async function fetchProjectDetailByAssignmentSubmission(assignmentSubmissionId) {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/assignmentsubmissions/project-detail/${assignmentSubmissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailProject(res.data.project || null);
      setTaxonomy(res.data.taxonomy || { objectives: [], standards: [], skills: [] });
      setRelatedEvents(res.data.relatedEvents || []);
      // Save the enrollment id from the assignment submission (if present)
      if (res.data.project && res.data.project.id) {
        // Fetch the assignment submission to get the enrollment id
        const subRes = await axios.get(`/api/assignmentsubmissions/${assignmentSubmissionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (subRes.data && subRes.data.submission && subRes.data.submission.enrollment) {
          setAssignmentSubmissionEnrollment(subRes.data.submission.enrollment);
        } else {
          setAssignmentSubmissionEnrollment(null);
        }
      } else {
        setAssignmentSubmissionEnrollment(null);
      }
    } catch (err) {
      setDetailProject(null);
      setTaxonomy({ objectives: [], standards: [], skills: [] });
      setRelatedEvents([]);
      setAssignmentSubmissionEnrollment(null);
    } finally {
      setDetailLoading(false);
    }
  }

  // Fetch project detail by projectSubmission id (for evaluate mode)
  async function fetchProjectDetailByProjectSubmission(projectSubmissionId) {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/projects/project-submission/${projectSubmissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailProject(res.data.project || null);
      setTaxonomy(res.data.taxonomy || { objectives: [], standards: [], skills: [] });
      setRelatedEvents(res.data.relatedEvents || []);
      // Save the enrollment id from the project submission
      if (res.data.projectSubmission && res.data.projectSubmission.enrollment) {
        setAssignmentSubmissionEnrollment(res.data.projectSubmission.enrollment);
      } else {
        setAssignmentSubmissionEnrollment(null);
      }
    } catch (err) {
      setDetailProject(null);
      setTaxonomy({ objectives: [], standards: [], skills: [] });
      setRelatedEvents([]);
      setAssignmentSubmissionEnrollment(null);
    } finally {
      setDetailLoading(false);
    }
  }

  function handleBackToList() {
    navigate('/projects-directory');
  }

  if (showDetail) {
    const command = query.get('command');
    const previewMode = command === 'preview';
    const restrictedGradeMode = command === 'gradeButRestricted';
    if (!isStudentRole && !gradeMode && !previewMode && !restrictedGradeMode) {
      return (
        <div style={{ maxWidth: 700, margin: '4rem auto', padding: 32, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', textAlign: 'center' }}>
          <h2 style={{ color: '#1D4E89', marginBottom: 16 }}>Access Restricted</h2>
          <div style={{ color: '#888', fontSize: 18 }}>Project display is only available for students.</div>
        </div>
      );
    }
    if (detailLoading || !detailProject) return <LoadingSpinner />;
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button onClick={handleBackToList} style={{ padding: '0.4rem 1.1rem', borderRadius: 6, border: '1px solid #1D4E89', background: '#fff', color: '#1D4E89', fontWeight: 600, cursor: 'pointer' }}>
            ← Projects List
          </button>
          {previewMode && canSeeFlags(userData, detailProject) && (() => {
            const flagInfo = getFlagButtonInfo(userData, detailProject);
            return (
              <button 
                onClick={() => setShowFlagModal(true)}
                style={{ 
                  padding: '0.4rem 1.1rem', 
                  borderRadius: 6, 
                  border: '1px solid #dc3545', 
                  background: '#fff', 
                  color: '#dc3545', 
                  fontWeight: 600, 
                  cursor: 'pointer' 
                }}
              >
                {flagInfo.buttonText}
              </button>
            );
          })()}
        </div>
        <ProjectDetail project={detailProject} taxonomy={taxonomy} relatedEvents={relatedEvents} activeTab={activeTab} setActiveTab={setActiveTab} userData={userData} gradeMode={gradeMode} assignmentSubmissionEnrollment={assignmentSubmissionEnrollment} assignmentSubmissionId={query.get('assignmentSubmission')} previewMode={previewMode} restrictedGradeMode={restrictedGradeMode} taxonomyItems={taxonomyItems} evaluateMode={command === 'evaluate'} />
        
        {/* Flag Modal */}
        {showFlagModal && (() => {
          const flagInfo = getFlagButtonInfo(userData, detailProject);
          return (
            <ProjectComments
              referenceId={detailProject.id}
              table="projects"
              userData={userData}
              isOpen={showFlagModal}
              onClose={() => setShowFlagModal(false)}
              relevantRole={flagInfo.relevantRole}
              defaultType={flagInfo.defaultType}
              onEvaluationComment={() => {
                setShowFlagModal(false);
              }}
            />
          );
        })()}
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
      <h1 style={{ color: '#1D4E89', fontSize: '2.2rem', marginBottom: 24 }}>Projects Directory</h1>
      {concentrationFilter && (
        <div style={{ 
          marginBottom: 16, 
          padding: '8px 12px', 
          backgroundColor: '#e3f2fd', 
          border: '1px solid #90caf9', 
          borderRadius: 6, 
          color: '#1565c0', 
          fontSize: 14 
        }}>
          Filtered by concentration: <strong>{getConcentrationName(concentrationFilter)}</strong>
          {isStudentRole && concentrationFilter === getStudentDefaultConcentration() && (
            <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.8 }}>
              (Your default concentration)
            </span>
          )}
        </div>
      )}
      <div style={{ marginBottom: 24, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by title or code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #ccc', width: 320 }}
        />
        <select
          value={concentrationFilter}
          onChange={e => setConcentrationFilter(e.target.value)}
          style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #ccc', minWidth: 200 }}
        >
          <option value="">All Concentrations</option>
          {concentrations.map(conc => (
            <option key={conc.id} value={conc.id}>
              {conc.title}
            </option>
          ))}
        </select>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', color: '#1D4E89', fontWeight: 600 }}>
              <th style={th}>Title</th>
              <th style={th}>Code</th>
              <th style={th}>Type(s)</th>
              <th style={th}>Possible Hours</th>
              <th style={th}>Concentration(s)</th>
              {!isStudentRole && <th style={th}>Created Date</th>}
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.length === 0 ? (
              <tr><td colSpan={!isStudentRole ? 7 : 6} style={{ textAlign: 'center', color: '#888', padding: 32 }}>No projects found.</td></tr>
            ) : filteredProjects.map(project => {
              const types = (project.types || []).join(', ');
              const possibleHours = Array.isArray(project.typedata) ? Math.max(...project.typedata.map(t => t.hours || 0)) : '';
              const concentrations = [project.concentration, ...(project.otherconcentrations || [])].filter(Boolean).join(', ');
              return (
                <tr key={project.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td}>{project.title}</td>
                  <td style={td}>{project.code}</td>
                  <td style={td}>{types}</td>
                  <td style={td}>{possibleHours}</td>
                  <td style={td}>{concentrations}</td>
                  {!isStudentRole && <td style={td}>{formatCreatedDate(project.createddate)}</td>}
                  <td style={td}>
                    <button
                      style={viewBtn}
                      onClick={() => {
                        const command = isStudentRole ? 'displayProject' : 'preview';
                        navigate(`/projects-directory?command=${command}&project_code=${project.code}`);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Helper to render video from URL (copied from ProjectBuilder.js)
function renderVideoFromUrl(url) {
  const w = 800;
  const h = 450;
  if (!url) return null;

  // Vimeo embed
  if (/vimeo\.com/.test(url)) {
    const id = url.split('/').pop();
    return (
      <iframe
        src={`https://player.vimeo.com/video/${id}`}
        width={w}
        height={h}
        frameBorder="0"
        allow="autoplay; fullscreen"
        allowFullScreen
        title="Vimeo Video"
      />
    );
  }

  // YouTube embed
  if (/youtube\.com|youtu\.be/.test(url)) {
    let id = url;
    if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1];
    else if (url.includes('v=')) id = url.split('v=')[1].split('&')[0];
    return (
      <iframe
        width={w}
        height={h}
        src={`https://www.youtube.com/embed/${id}`}
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen
        title="YouTube Video"
      />
    );
  }

  // Fallback to HTML5 <video> tag
  return (
    <video
      src={url}
      width={w}
      height={h}
      controls
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      style={{ backgroundColor: "#000" }}
    />
  );
}

function isStudent(userData) {
  if (!userData || !userData.accesscards || !userData.default_accesscard_id) return false;
  const card = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
  return card && card.role === 'Student';
}

function ProjectDetail({ project, taxonomy, relatedEvents, activeTab, setActiveTab, userData, gradeMode, assignmentSubmissionEnrollment, assignmentSubmissionId, previewMode, restrictedGradeMode, taxonomyItems, evaluateMode = false }) {
  const [assignmentsByType, setAssignmentsByType] = React.useState(null);
  const [assignmentsLoading, setAssignmentsLoading] = React.useState(false);
  const [assignmentsError, setAssignmentsError] = React.useState('');
  const [activeAssignment, setActiveAssignment] = React.useState(null);
  const [activeSubmission, setActiveSubmission] = React.useState(null);
  const [resubmitMode, setResubmitMode] = React.useState(false);
  const [selectedType, setSelectedType] = React.useState(null);
  const [existingProjectSubmission, setExistingProjectSubmission] = React.useState(null);
  const validEnrollment = getValidEnrollment(userData);
  const assignmentViewerRef = useRef(null);
  const [showCommentsModal, setShowCommentsModal] = React.useState(false);
  const [updatingAllowResubmit, setUpdatingAllowResubmit] = React.useState(null);
  const [hasEvaluationComment, setHasEvaluationComment] = React.useState(null); // null = unknown, true/false = fetched
  const isStudentRole = userData && userData.accesscards && userData.default_accesscard_id &&
    userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id)?.role === 'Student';

  // Merge taxonomy data from both old and new sources
  const mergedTaxonomy = React.useMemo(() => {
    if (!project || !taxonomyItems) return { objectives: [], standards: [], skills: [] };
    
    const merged = { objectives: [], standards: [], skills: [] };
    const seenIds = new Set();

    // Helper function to add item if not already seen
    const addIfNotSeen = (item, type) => {
      if (item && item.id && !seenIds.has(item.id)) {
        seenIds.add(item.id);
        merged[type].push(item);
      }
    };

    // Process new taxonomy field (JSONB)
    if (project.taxonomy && Array.isArray(project.taxonomy)) {
      project.taxonomy.forEach(taxItem => {
        if (taxItem.taxonomy_id) {
          const fullTaxonomyItem = taxonomyItems.find(item => item.id === taxItem.taxonomy_id);
          if (fullTaxonomyItem) {
            const typeKey = fullTaxonomyItem.type.toLowerCase() + 's';
            if (merged[typeKey]) {
              addIfNotSeen(fullTaxonomyItem, typeKey);
            }
          }
        }
      });
    }

    // Process old separate fields (backward compatibility)
    if (project.objectives && Array.isArray(project.objectives)) {
      project.objectives.forEach(objId => {
        const fullTaxonomyItem = taxonomyItems.find(item => item.id === objId);
        if (fullTaxonomyItem) {
          addIfNotSeen(fullTaxonomyItem, 'objectives');
        }
      });
    }

    if (project.standards && Array.isArray(project.standards)) {
      project.standards.forEach(stdId => {
        const fullTaxonomyItem = taxonomyItems.find(item => item.id === stdId);
        if (fullTaxonomyItem) {
          addIfNotSeen(fullTaxonomyItem, 'standards');
        }
      });
    }

    if (project.skills && Array.isArray(project.skills)) {
      project.skills.forEach(skillId => {
        const fullTaxonomyItem = taxonomyItems.find(item => item.id === skillId);
        if (fullTaxonomyItem) {
          addIfNotSeen(fullTaxonomyItem, 'skills');
        }
      });
    }

    console.log('ProjectsDirectory - Merged taxonomy data:', {
      projectId: project?.id,
      newTaxonomy: project?.taxonomy,
      oldObjectives: project?.objectives,
      oldStandards: project?.standards,
      oldSkills: project?.skills,
      taxonomyItemsCount: taxonomyItems?.length,
      mergedResult: merged
    });
    return merged;
  }, [project, taxonomyItems]);

  const availableTypes = React.useMemo(() => {
    if (!project.typedata || !Array.isArray(project.typedata)) return [];
    return project.typedata.map(type => ({
      name: type.type,
      hours: type.hours || 0
    }));
  }, [project.typedata]);

  React.useEffect(() => {
    if (availableTypes.length > 0 && !selectedType) {
      setSelectedType(availableTypes[0].name);
    }
  }, [availableTypes, selectedType]);

  // Set selected type to existing project submission type if it exists
  React.useEffect(() => {
    if (existingProjectSubmission && existingProjectSubmission.type) {
      setSelectedType(existingProjectSubmission.type);
    }
  }, [existingProjectSubmission]);

  // Helper to refresh assignments list
  const refreshAssignments = React.useCallback(() => {
    setAssignmentsLoading(true);
    setAssignmentsError('');
    setAssignmentsByType(null);
    const fetchAssignments = async () => {
      try {
        const token = localStorage.getItem('token');
        const enrollmentId = ((gradeMode || restrictedGradeMode) && assignmentSubmissionEnrollment) ? assignmentSubmissionEnrollment : (validEnrollment ? validEnrollment.enrollment_id : undefined);
        
        // In preview mode, we don't need an enrollment - just show all assignments
        if (!enrollmentId && !previewMode) {
          setAssignmentsError('No enrollment found.');
          setAssignmentsByType(null);
          setAssignmentsLoading(false);
          return;
        }
        
        // For preview mode, don't include enrollment parameter
        const url = previewMode 
          ? `/api/projects/${project.code}/assignments`
          : `/api/projects/${project.code}/assignments?enrollment=${enrollmentId}`;
          
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Refreshed assignments data:', res.data);
        setAssignmentsByType(res.data.assignmentsByType || {});
        setExistingProjectSubmission(res.data.existingProjectSubmission || null);
      } catch (err) {
        setAssignmentsError('Failed to load assignments.');
        setAssignmentsByType(null);
      } finally {
        setAssignmentsLoading(false);
      }
    };
    fetchAssignments();
  }, [gradeMode, restrictedGradeMode, assignmentSubmissionEnrollment, validEnrollment, project.code, previewMode]);

  React.useEffect(() => {
    if (activeTab !== 'Assignments') return;
    refreshAssignments();
  }, [activeTab, project.code, validEnrollment, gradeMode, restrictedGradeMode, assignmentSubmissionEnrollment, refreshAssignments]);

  // Check if project submission has Evaluation comment (for Concentration/Capstone awaiting evaluation)
  React.useEffect(() => {
    if (!existingProjectSubmission || existingProjectSubmission.status === 'complete') {
      setHasEvaluationComment(null);
      return;
    }
    const isConcentrationOrCapstone = existingProjectSubmission.type === 'Concentration' || existingProjectSubmission.type === 'Capstone';
    if (!isConcentrationOrCapstone) {
      setHasEvaluationComment(null);
      return;
    }
    if (evaluateMode) {
      setHasEvaluationComment(false); // Evaluate mode implies it needs evaluation
      return;
    }
    axios.get(`/api/comments?reference_id=${existingProjectSubmission.id}&table=projectsubmissions`)
      .then(res => {
        const comments = res.data.comments || [];
        const hasEval = comments.some(c => c.type === 'Evaluation');
        setHasEvaluationComment(hasEval);
      })
      .catch(() => setHasEvaluationComment(null));
  }, [existingProjectSubmission?.id, existingProjectSubmission?.status, existingProjectSubmission?.type, evaluateMode]);

  // Auto-open assignment in grade mode or restricted grade mode if assignmentSubmissionId is present
  React.useEffect(() => {
    if ((!gradeMode && !restrictedGradeMode) || !assignmentSubmissionId || !assignmentsByType) return;
    // Find the assignment and submission for this assignmentSubmissionId
    for (const type in assignmentsByType) {
      for (const { assignment, submission } of assignmentsByType[type]) {
        if (submission && submission.id === assignmentSubmissionId) {
          setActiveAssignment(assignment);
          setActiveSubmission(submission);
          setActiveTab('Assignments');
          // Scroll to assignment after a short delay to ensure render
          setTimeout(() => {
            if (assignmentViewerRef.current) {
              assignmentViewerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 300);
          return;
        }
      }
    }
  }, [gradeMode, restrictedGradeMode, assignmentSubmissionId, assignmentsByType]);

  const handleAssignmentAction = (assignment, submission, isResubmit = false) => {
    setActiveAssignment(assignment);
    setActiveSubmission(submission || null);
    setResubmitMode(isResubmit);
  };

  const handleToggleAllowResubmission = async (submissionId, currentValue) => {
    if (!submissionId) return;
    setUpdatingAllowResubmit(submissionId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/assignmentsubmissions/${submissionId}`, {
        allow_resubmission: !currentValue
      }, { headers: { Authorization: `Bearer ${token}` } });
      refreshAssignments();
    } catch (err) {
      console.error('Error updating allow resubmission:', err);
      alert(err.response?.data?.error || 'Failed to update. Please try again.');
    } finally {
      setUpdatingAllowResubmit(null);
    }
  };

  const handleCloseAssignmentViewer = () => {
    setActiveAssignment(null);
    setActiveSubmission(null);
    setResubmitMode(false);
    if (activeTab === 'Assignments') {
      setAssignmentsLoading(true);
      setAssignmentsError('');
      setAssignmentsByType(null);
      const fetchAssignments = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`/api/projects/${project.code}/assignments?enrollment=${validEnrollment.enrollment_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAssignmentsByType(res.data.assignmentsByType || {});
          setExistingProjectSubmission(res.data.existingProjectSubmission || null);
        } catch (err) {
          setAssignmentsError('Failed to load assignments.');
          setAssignmentsByType(null);
        } finally {
          setAssignmentsLoading(false);
        }
      };
      fetchAssignments();
    }
  };

  const assignmentsForSelectedType = React.useMemo(() => {
    if (!assignmentsByType || !selectedType) return [];
    return assignmentsByType[selectedType] || [];
  }, [assignmentsByType, selectedType]);

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#1D4E89', fontWeight: 600, fontSize: 18, marginBottom: 4 }}>
            Concentration : {project.concentration}
          </div>
          <div style={{ fontWeight: 700, fontSize: 28, marginBottom: 8 }}>{project.title}</div>
          <div style={{ marginBottom: 12 }}>
            {renderVideoFromUrl(project.medialink)}
          </div>

        </div>
        <div style={{ marginLeft: 32, minWidth: 160, textAlign: 'center' }}>
          <div style={{ fontWeight: 600, color: '#1D4E89', marginBottom: 8 }}>{project.code}</div>
          {project.qrcode && <img src={project.qrcode} alt="QR Code" style={{ width: 120, height: 120, marginBottom: 8 }} />}
          <div style={{ fontSize: 13, color: '#888' }}>Share this Project!</div>
        </div>
      </div>
      {/* Project Submission Status */}
      {existingProjectSubmission && (
        <div style={{
          width: '100%',
          marginTop: 16,
          padding: '12px 16px',
          backgroundColor: (() => {
            if (existingProjectSubmission.status === 'complete') return '#d4edda';
            const needsEval = (existingProjectSubmission.type === 'Concentration' || existingProjectSubmission.type === 'Capstone') && existingProjectSubmission.status === 'started' && hasEvaluationComment === false;
            return needsEval ? '#fff8e1' : '#fff3cd';
          })(),
          border: (() => {
            if (existingProjectSubmission.status === 'complete') return '1px solid #c3e6cb';
            const needsEval = (existingProjectSubmission.type === 'Concentration' || existingProjectSubmission.type === 'Capstone') && existingProjectSubmission.status === 'started' && hasEvaluationComment === false;
            return needsEval ? '1px solid #ffcc02' : '1px solid #ffeaa7';
          })(),
          borderRadius: 6,
          color: (() => {
            if (existingProjectSubmission.status === 'complete') return '#155724';
            const needsEval = (existingProjectSubmission.type === 'Concentration' || existingProjectSubmission.type === 'Capstone') && existingProjectSubmission.status === 'started' && hasEvaluationComment === false;
            return needsEval ? '#e65100' : '#856404';
          })(),
          position: 'relative'
        }}>
          {/* Comments Button - now inside the status box, top right */}
          <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
            <button
              style={{
                padding: 0,
                background: 'none',
                border: '2px solid #1D4E89',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.2s ease'
              }}
              onClick={() => setShowCommentsModal(true)}
              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
              title="View Comments"
            >
              <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="12" fill="#F5F7FA"/>
                <path d="M12 16C12 13.7909 13.7909 12 16 12H32C34.2091 12 36 13.7909 36 16V26C36 28.2091 34.2091 30 32 30H22L16 36V30H16C13.7909 30 12 28.2091 12 26V16Z" fill="white" stroke="#4A5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="18" cy="21" r="1.5" fill="#4A5568"/>
                <circle cx="24" cy="21" r="1.5" fill="#4A5568"/>
                <circle cx="30" cy="21" r="1.5" fill="#4A5568"/>
              </svg>
            </button>
            <ProjectComments
              referenceId={existingProjectSubmission.id}
              table="projectsubmissions"
              userData={userData}
              isOpen={showCommentsModal}
              onClose={() => setShowCommentsModal(false)}
              relevantRole={gradeMode ? (userData?.accesscards?.find(ac => ac.accesscard_id === userData.default_accesscard_id)?.role || 'Instructor') : undefined}
              onEvaluationComment={() => {
                setShowCommentsModal(false);
                setHasEvaluationComment(true);
                refreshAssignments();
              }}
            />
          </div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Project Status: {existingProjectSubmission.status === 'complete' ? 'Completed' : 'In Progress'}
          </div>
          {existingProjectSubmission.status === 'complete' && existingProjectSubmission.hoursreceived && (
            <div style={{ color: '#11AF07', fontWeight: 500 }}>
              {existingProjectSubmission.hoursreceived} hours received
            </div>
          )}
          {existingProjectSubmission.status === 'started' && (() => {
            const needsEval = (existingProjectSubmission.type === 'Concentration' || existingProjectSubmission.type === 'Capstone') && hasEvaluationComment === false;
            if (needsEval) {
              return (
                <div style={{ fontSize: 14 }}>
                  {evaluateMode ? (
                    <>Student has completed all assignments. Only the evaluation comment is required (for {existingProjectSubmission.type}).</>
                  ) : isStudentRole ? (
                    <>You have completed all assignments, but the instructor must leave an evaluation comment for you to receive the hours. Use the comments icon to communicate with instructor.</>
                  ) : (
                    <>Continue working on assignments to complete this project</>
                  )}
                </div>
              );
            }
            return (
              <div style={{ fontSize: 14 }}>
                Continue working on assignments to complete this project
              </div>
            );
          })()}
        </div>
      )}
      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 16, display: 'flex', gap: 2 }}>
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 18px',
              border: 'none',
              borderBottom: activeTab === tab ? '3px solid #1D4E89' : '3px solid transparent',
              background: 'none',
              fontWeight: activeTab === tab ? 700 : 500,
              color: activeTab === tab ? '#1D4E89' : '#444',
              fontSize: 16,
              cursor: 'pointer',
              outline: 'none',
              transition: 'color 0.2s'
            }}
          >
            {tab}
          </button>
        ))}
      </div>
      <div style={{ minHeight: 120, padding: 8 }}>
        {/* Tab content scaffold */}
        {activeTab === 'Description' && (
          <div>{project.shortdescription}</div>
        )}
        {activeTab === 'Assignments' && (
          <div>
            {(!validEnrollment && !gradeMode && !restrictedGradeMode && !previewMode) && <div style={{ color: '#888' }}>No valid enrollment found.</div>}
            {((validEnrollment || gradeMode || restrictedGradeMode || previewMode) && assignmentsLoading) && <LoadingSpinner />}
            {((validEnrollment || gradeMode || restrictedGradeMode || previewMode) && assignmentsError) && <div style={{ color: 'red' }}>{assignmentsError}</div>}
            {((validEnrollment || gradeMode || restrictedGradeMode || previewMode) && assignmentsByType && Object.keys(assignmentsByType).length === 0) && <div style={{ color: '#888' }}>No assignments found for this project.</div>}
            {((validEnrollment || gradeMode || restrictedGradeMode || previewMode) && assignmentsByType && availableTypes.length > 0) && (
              <>
                {/* Type Selector */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{display: 'inline-block', fontWeight: 600, fontSize: 16, color: '#1D4E89', marginBottom: 8 }}>
                    {existingProjectSubmission ? 'Project Type' : 'Select Project Type:'}
                  </div>
                  {existingProjectSubmission && availableTypes.length > 1 && (
                    <div style={{
                      display: 'inline-block',
                      width: 'auto',
                      marginLeft: 8,
                      marginBottom: 6,
                      padding: '4px 4px 4px 4px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: 6,
                      color: '#856404',
                      fontSize: 11
                    }}>
                      You have already started this project with type: <strong>{existingProjectSubmission.type}</strong>.
                      You cannot change the project type once started.
                    </div>
                  )}
                  {(!existingProjectSubmission && availableTypes.length > 1) && (
                    <div style={{
                      marginBottom: 12,
                      padding: '8px 12px',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #90caf9',
                      borderRadius: 6,
                      color: '#1565c0',
                      fontSize: 14
                    }}>
                      You may only do one project type.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {availableTypes.map(type => {
                      const isSelected = selectedType === type.name;
                      const isDisabled = existingProjectSubmission && !isSelected;
                      return (
                        <button
                          key={type.name}
                          onClick={() => !isDisabled && setSelectedType(type.name)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: 20,
                            border: isSelected ? '2px solid #1D4E89' : '2px solid #e0e0e0',
                            background: isSelected ? '#1D4E89' : (isDisabled ? '#f5f5f5' : '#fff'),
                            color: isSelected ? '#fff' : (isDisabled ? '#999' : '#1D4E89'),
                            fontWeight: isSelected ? 600 : 500,
                            fontSize: 14,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            outline: 'none',
                            opacity: isDisabled ? 0.6 : 1
                          }}
                        >
                          {type.name} ({type.hours} hours)
                        </button>
                      );
                    })}
                  </div>
                </div>
                {assignmentsForSelectedType.length > 0 ? (
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 12, background: '#fafbfc' }}>
                    {assignmentsForSelectedType.map(({ assignment, submission }) => {
                      const canResubmit = submission && !gradeMode && !previewMode && !restrictedGradeMode && (
                        submission.graderesult === 'needsEvaluation' ||
                        assignment.gradingresulttype === 'Survey' ||
                        submission.allow_resubmission === true
                      );
                      return (
                        <div key={assignment.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', padding: '8px 0' }}>
                          <div>
                            <div style={{ fontWeight: 500 }}>{assignment.title}</div>
                            <div style={{ color: '#555', fontSize: 14 }}>{(assignment.gradingresulttype == "Points") ? "Assessment" : assignment.gradingresulttype} &nbsp;|&nbsp; {assignment.hours || 0} hour{assignment.hours === 1 ? '' : 's'} possible</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {submission ? (
                              <>
                                {/* Status text based on graderesult */}
                                <span style={{
                                  color: submission.graderesult === 'needsEvaluation' ? '#f39c12' : '#1D4E89',
                                  fontWeight: 500
                                }}>
                                  {submission.graderesult === 'needsEvaluation' ? 'Pending' : 'Completed'}
                                </span>
                                {/* Hours text based on graderesult and hours received */}
                                <span style={{
                                  color: submission.graderesult === 'needsEvaluation' ? '#324158' :
                                    (submission.hoursreceived > 0 ? '#11AF07' : '#324158'),
                                  fontSize: 14
                                }}>
                                  {submission.graderesult === 'needsEvaluation' ? 'waiting on instructor' :
                                    `${submission.hoursreceived} hours received`}
                                </span>
                                <button style={{ padding: '0.3rem 1rem', borderRadius: 4, border: '1px solid #1D4E89', background: '#fff', color: '#1D4E89', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleAssignmentAction(assignment, submission)}>View</button>
                                {canResubmit && (
                                  <button style={{ padding: '0.3rem 1rem', borderRadius: 4, border: '1px solid #0d9488', background: '#fff', color: '#0d9488', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleAssignmentAction(assignment, submission, true)}>Resubmit</button>
                                )}
                                {/* Instructor: Allow Resubmission (Points, already graded) */}
                                {gradeMode && assignment.gradingresulttype === 'Points' && submission.graderesult && String(submission.graderesult).includes('%') && (
                                  <button
                                    style={{
                                      padding: '0.3rem 0.8rem',
                                      borderRadius: 4,
                                      border: `2px solid ${submission.allow_resubmission ? '#4caf50' : '#ddd'}`,
                                      background: submission.allow_resubmission ? '#4caf50' : '#f5f5f5',
                                      color: submission.allow_resubmission ? '#fff' : '#666',
                                      fontWeight: 600,
                                      cursor: updatingAllowResubmit === submission.id ? 'not-allowed' : 'pointer',
                                      fontSize: 13,
                                      opacity: updatingAllowResubmit === submission.id ? 0.7 : 1
                                    }}
                                    onClick={() => handleToggleAllowResubmission(submission.id, submission.allow_resubmission)}
                                    disabled={updatingAllowResubmit === submission.id}
                                    title={submission.allow_resubmission ? 'Click to disable resubmission' : 'Click to allow student to resubmit'}
                                  >
                                    {updatingAllowResubmit === submission.id ? 'Updating...' : (submission.allow_resubmission ? '✓ Allow Resubmission' : 'Allow Resubmission')}
                                  </button>
                                )}
                              </>
                            ) : (
                              (gradeMode || previewMode) ? (
                                <button style={{ padding: '0.3rem 1rem', borderRadius: 4, border: '1px solid #1D4E89', background: '#fff', color: '#1D4E89', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleAssignmentAction(assignment, null)}>View</button>
                              ) : (
                                <button style={{ padding: '0.3rem 1rem', borderRadius: 4, border: '1px solid #1D4E89', background: '#1D4E89', color: '#fff', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleAssignmentAction(assignment, null)}>Start</button>
                              )
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ color: '#888', textAlign: 'center', padding: 24 }}>No assignments found for the selected type.</div>
                )}
              </>
            )}
            {activeAssignment && (
              <div ref={assignmentViewerRef} style={{ marginTop: 32, border: '2px solid #1D4E89', borderRadius: 12, background: '#f9fbff', padding: 24 }}>
                <AssignmentViewer
                  assignment={activeAssignment}
                  mode={gradeMode ? 'grade' : (restrictedGradeMode ? 'view' : (previewMode ? 'view' : (activeSubmission && !resubmitMode ? 'view' : 'submit')))}
                  interactive={gradeMode || previewMode || restrictedGradeMode ? false : (resubmitMode || !activeSubmission)}
                  submission={resubmitMode ? activeSubmission : activeSubmission}
                  enrollmentId={validEnrollment ? validEnrollment.enrollment_id : undefined}
                  projectId={project.id}
                  projectType={selectedType}
                  userData={userData}
                  projectData={project}
                  onClose={handleCloseAssignmentViewer}
                  onSubmit={handleCloseAssignmentViewer}
                  onGradingUpdate={refreshAssignments}
                  showAnswers={gradeMode || previewMode || restrictedGradeMode}
                  allowGrading={gradeMode && !restrictedGradeMode}
                  resubmitSubmissionId={resubmitMode && activeSubmission ? activeSubmission.id : null}
                />
              </div>
            )}
          </div>
        )}
        {activeTab === 'Procedures' && (
          <div dangerouslySetInnerHTML={{ __html: project.procedures || '' }} />
        )}
        {activeTab === 'Objectives' && (
          <div>
            {mergedTaxonomy.objectives.length > 0
              ? mergedTaxonomy.objectives.map((obj, i) => (
                <div key={obj.id} style={{ marginBottom: 8 }}>
                  <b>{obj.title}</b>{obj.description ? ': ' + obj.description : ''}
                </div>
              ))
              : <span>No objectives listed.</span>}
          </div>
        )}
        {activeTab === 'Standards' && (
          <div>
            {mergedTaxonomy.standards.length > 0
              ? mergedTaxonomy.standards.map((std, i) => (
                <div key={std.id} style={{ marginBottom: 8 }}>
                  <b>{std.title}</b>{std.description ? ': ' + std.description : ''}
                </div>
              ))
              : <span>No standards listed.</span>}
          </div>
        )}
        {activeTab === 'Skills' && (
          <div>
            {mergedTaxonomy.skills.length > 0
              ? mergedTaxonomy.skills.map((skill, i) => (
                <div key={skill.id} style={{ marginBottom: 8 }}>
                  <b>{skill.title}</b>{skill.description ? ': ' + skill.description : ''}
                </div>
              ))
              : <span>No skills listed.</span>}
          </div>
        )}
        {activeTab === 'Concentrations' && (
          <div>
            {Array.isArray(project.otherconcentrations) && project.otherconcentrations.length > 0
              ? project.otherconcentrations.map((conc, i) => <div key={i} style={{ marginBottom: 8 }}>{conc}</div>)
              : <span>No other concentrations listed.</span>}
          </div>
        )}
        {activeTab === 'Related Events' && (
          <div>
            {relatedEvents.length > 0
              ? relatedEvents.map(ev => (
                <div key={ev.id} style={{ marginBottom: 12, padding: 8, border: '1px solid #e0e0e0', borderRadius: 6 }}>
                  <div style={{ fontWeight: 600 }}>{ev.title}</div>
                  <div style={{ color: '#555' }}>{ev.location}</div>
                  <div style={{ color: '#888', fontSize: 13 }}>{dateTimeToMonDDYYYYWithTime(ev.date, false)} {ev.start_time && ev.end_time ? `(${convertTo12HourFormat(ev.start_time)} - ${convertTo12HourFormat(ev.end_time)})` : ''}</div>
                </div>
              ))
              : <span>No related events listed.</span>}
          </div>
        )}
        {activeTab === 'Other Resources' && (
          <div dangerouslySetInnerHTML={{ __html: project.additionalresources || '' }} />
        )}
      </div>
    </div>
  );
}

const th = { padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' };
const td = { padding: '10px 8px', textAlign: 'left', fontSize: 15 };
const viewBtn = {
  padding: '0.4rem 1.1rem',
  borderRadius: 6,
  border: '1px solid #1D4E89',
  background: '#fff',
  color: '#1D4E89',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 0.2s',
};

export default ProjectsDirectory; 